import { alpha } from '@mui/material/styles';
import classNames from 'classnames';
import { useMemo, useState } from 'react';

import { hasSolution as getHasSolution } from 'my-core/editor-utils';

import GatedContent from 'my-components/OnlineCourse/InCourseExperience/GatedContent';
import Editor, { LoadingFallback, pluginPresets, SolutionStepsEditor } from 'my-editor';
import copyManager from 'my-editor/copyManager';

import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';

import { faCopy, faFileArrowDown, faKey } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PREVIEW_HEIGHT_UNITS = 12;
const rootCss = ({ palette, spacing }) => ({
  position: 'relative',

  '&.root_preview > .CLL-content': { height: spacing(PREVIEW_HEIGHT_UNITS), overflow: 'hidden' },
  '.CLL-buttons': {
    position: 'sticky',
    top: spacing(2),
    zIndex: 2,
    marginBottom: spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing(1),
    '& > .MuiButton-root': { background: palette.background.paper },
  },
  '.CLL-previewBlocker': {
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1,
    position: 'relative',
    marginTop: spacing(-PREVIEW_HEIGHT_UNITS),
    background: `linear-gradient(to bottom, ${alpha(palette.background.paper, 0)} 0%, ${
      palette.background.paper
    } ${spacing(PREVIEW_HEIGHT_UNITS)})`,
    padding: spacing(PREVIEW_HEIGHT_UNITS + 3, 2, 3),
  },
});

export default function CourseLessonLecture(props) {
  const {
    className,
    content,
    enableCopy,
    fetching,
    fetchingBooklet,
    onPrintBooklet,
    previewMode,
    showSolutions: showSolutionsProp,
    ...rest
  } = props;

  const [showSolutions, setShowSolutions] = useState(false);

  const hasSolution = useMemo(() => !previewMode && getHasSolution(content), [content, previewMode]);

  function copyContent() {
    copyManager.addContentToClipboard(content);
  }

  function toggleSolutions() {
    setShowSolutions(!showSolutions);
  }

  const withSolutions = showSolutionsProp || showSolutions;

  return (
    <div className={classNames(className, { root_preview: previewMode })} css={rootCss}>
      {!!(hasSolution || enableCopy || onPrintBooklet) && (
        <div className="CLL-buttons">
          {onPrintBooklet && (
            <Button
              disabled={!!fetchingBooklet}
              onClick={onPrintBooklet}
              size="small"
              startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
              variant="outlined"
            >
              Download Notes
            </Button>
          )}
          {hasSolution && (
            <Button
              onClick={toggleSolutions}
              size="small"
              startIcon={<FontAwesomeIcon icon={faKey} />}
              variant="outlined"
            >
              {`${withSolutions ? 'Hide' : 'Show'} Solutions`}
            </Button>
          )}
          {enableCopy && (
            <Tooltip title="Copy Content">
              <Fab color="secondary" onClick={copyContent} size="small" variant="extended">
                <FontAwesomeIcon icon={faCopy} />
              </Fab>
            </Tooltip>
          )}
        </div>
      )}
      <div className="CLL-content">
        {content ?
          hasSolution ?
            <SolutionStepsEditor showSolutions={withSolutions} value={content} />
          : <Editor
              canToggleSolutions
              fallback={<LoadingFallback value={content} />}
              plugins={pluginPresets.ALL}
              readOnly
              showSolutions={withSolutions}
              value={content}
            />

        : fetching && <LoadingFallback lines={30} />}
      </div>
      {previewMode && (
        <div className="CLL-previewBlocker">
          <GatedContent actionText="read this lecture" activityText="Content" hideIcon {...rest} />
        </div>
      )}
    </div>
  );
}
